import React, { useState, useEffect, useCallback, useRef } from "react";
import ReactFlow, {
  addEdge,
  MiniMap,
  Controls,
  //@ts-ignore
  ControlButton,
  ReactFlowProvider,
  Background,
  useNodesState,
  useEdgesState,
} from "react-flow-renderer";
import { ReactComponent as DeleteIcon } from "../../../assets/icons/delete.svg";
import UserService from "../../../services/UserService";
import BasicNode from "../../../components/Blocks/BasicNode";
import IfNode from "../../../components/Blocks/Logic/IfNode";
import EndNode from "../../../components/Blocks/System/EndNode";
import StartNode from "../../../components/Blocks/System/StartNode";
import ProgramSidebar from "./ProgramSidebar";
import CustomEdge from "../../../components/Edges/CustomEdge";
import {
  useActionsByBlockLibrary,
  useActionsGroupedByBlockLibrary,
  useActiveBlock,
  useDeviceGroup,
  useDeviceType,
  useProgram,
  useProgramDone,
} from "../../../Api/newSockets.api";
import { ReactComponent as PlayIcon } from "../../../assets/icons/play.svg";
import { ReactComponent as PauseIcon } from "../../../assets/icons/pause.svg";
import { ReactComponent as ArrowLeftIcon } from "../../../assets/icons/arrow_left.svg";
import { useLocation } from "react-router-dom";
import { savePathToLocalStorage } from "../../../utils/helpers";
import useCheckMobileScreen from "../../../hooks/useCheckScreen";
import DesktopOnlyMessage from "../../../components/DesktopOnlyMessage/DesktopOnlyMessage";
import MonacoEditor from "@monaco-editor/react";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

enum InputType {
  string = 0,
  dropdown = 2,
}

const BgColor = "#1b2023";

const nodeTypes = {
  basicNode: BasicNode,
  ifNode: IfNode,
  startNode: StartNode,
  endNode: EndNode,
};

const edgeTypes = {
  customEdge: CustomEdge,
};

type EditorProps = {
  match?: any;
  programId?: any;
  readMode?: boolean;
  classNameWrapper?: string;
};

const getId = () => `node_${+new Date()}`;

const Editor = (props: EditorProps) => {
  savePathToLocalStorage();
  const reactFlowWrapper = useRef(null);
  const [reactFlowInstance, setReactFlowInstance] = useState(null);
  const { activeBlock } = useActiveBlock();
  const { getProgram, updateProgram, runProgram, stopProgram } = useProgram();
  const { programDone } = useProgramDone();
  const { getDeviceGroup } = useDeviceGroup();
  const { actionsGroupedByBlockLibrary } = useActionsGroupedByBlockLibrary();
  const currentUserDevice = useCheckMobileScreen();

  const { program } = getProgram(
    parseInt(props.match?.params?.id) || props.programId
  );
  // Gets group
  const group = getDeviceGroup(program?.deviceGroupId).deviceGroup;
  // Gets device with group id - default device
  const defaultDeviceId = group?.devices[0]?.id;

  // State for selected device - initialize from program or default
  const [selectedDeviceId, setSelectedDeviceId] = useState<number | null>(null);

  const [nodes, setNodes, onNodesChange] = useNodesState(
    program && program.data.nodes
  );
  const [edges, setEdges, onEdgesChange] = useEdgesState(
    program && program.data.edges
  );

  const [running, setRunning] = useState(false);

  const [scope, setScope] = useState([]);

  // Code editor state
  const [editingMode, setEditingMode] = useState(false);
  const [editingNodeId, setEditingNodeId] = useState<string | null>(null);
  const [nodeCode, setNodeCode] = useState<string>("");

  // Initialize selected device from program when it loads
  useEffect(() => {
    if (program && program.deviceId) {
      setSelectedDeviceId(program.deviceId);
    } else if (defaultDeviceId) {
      setSelectedDeviceId(defaultDeviceId);
    }
  }, [program, defaultDeviceId]);

  // Define onEditCode callback early
  const onEditCode = useCallback((nodeId: string, block: any) => {
    setNodes((currentNodes) => {
      const node = currentNodes.find((n) => n.id === nodeId);
      if (node) {
        const existingCode = node.data.block.code;
        console.log("Opening code editor for node:", nodeId);
        console.log("Existing code:", existingCode);
        setEditingNodeId(nodeId);
        setNodeCode(existingCode || "// Write your code here\n");
        setEditingMode(true);
      }
      return currentNodes;
    });
  }, []);

  useEffect(() => {
    if (program && onEditCode) {
      if (program.data.nodes) {
        setNodes(
          program.data.nodes.map((nd) => {
            nd.data = {
              ...nd.data,
              onDeleteNode: onDeleteNode,
              onEditCode: onEditCode,
            };
            return nd;
          })
        );
      }
      if (program.data.edges) {
        setEdges(
          program.data.edges.map((edg) => {
            edg.data = {
              onDeleteEdge: onDeleteEdge,
            };
            return edg;
          })
        );
      }
    }
  }, [program, onEditCode]);

  useEffect(() => {
    if (nodes && activeBlock) {
      console.log("activeBlock", { activeBlock });
      hightlightNode(activeBlock);
    }
  }, [activeBlock]);

  const hightlightNode = (node) => {
    setNodes(
      nodes.map((el) => {
        if (el.id == node.id) {
          el.style = {
            boxShadow: "0 0 20px -10px darkcyan",
            WebkitAnimation: "blink 0.7s infinite alternate",
            animation: "blink 0.7s infinite alternate",
            border: "1px solid cyan",
          };
        } else {
          el.style = {
            boxShadow: "unset",
            animation: "unset",
            borderColor: "unset",
          };
        }
        return el;
      })
    );
  };
  const onDragOver = (event) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = "move";
  };
  const onScopeChange = (variable: any) => {
    if (!scope.includes(variable)) {
      setScope([...scope, variable]);
      console.log({ scope });
    } else {
      alert("Variable already exists. Please choose a different name.");
    }
  };

  const onDrop = useCallback(
    (event) => {
      event.preventDefault();
      const reactFlowBounds = reactFlowWrapper.current.getBoundingClientRect();
      const type = event.dataTransfer.getData("node/type");
      const data = JSON.parse(event.dataTransfer.getData("node/data"));
      console.log("type ", type, "data", data);
      const position = reactFlowInstance.project({
        x: event.clientX - reactFlowBounds.left,
        y: event.clientY - reactFlowBounds.top,
      });

      const newNode = {
        id: getId(),
        type,
        position,
        data: {
          block: data,
          onScopeChange: onScopeChange,
          onDeleteNode: onDeleteNode,
          onEditCode: onEditCode,
        },
      };
      setNodes((nds) => nds.concat(newNode));
    },
    [reactFlowInstance, onEditCode]
  );

  const onAdd = useCallback(
    (node) => {
      var newId = getId();
      const newNode = {
        id: newId,
        type: node.type,
        position: {
          x: window.innerWidth * 0.1,
          y: window.innerHeight * 0.1,
        },
        data: {
          block: node.data,
          id: newId,
          onScopeChange: onScopeChange,
          onDeleteNode: onDeleteNode,
          onEditCode: onEditCode,
        },
      };
      setNodes((nds) => nds.concat(newNode));
    },
    [setNodes, onEditCode]
  );

  const onCodeChange = (newCode: string | undefined) => {
    if (newCode !== undefined) {
      setNodeCode(newCode);
      // Auto-save code to node state
      if (editingNodeId) {
        setNodes((nds) =>
          nds.map((node) => {
            if (node.id === editingNodeId) {
              return {
                ...node,
                data: {
                  ...node.data,
                  block: {
                    ...node.data.block,
                    code: newCode,
                  },
                },
              };
            }
            return node;
          })
        );
      }
    }
  };

  const closeCodeEditor = () => {
    setEditingMode(false);
    setEditingNodeId(null);
    setNodeCode("");
  };

  const saveData = () => {
    // Save program with selected device ID
    var newProgram = {
      ...program,
      deviceId: selectedDeviceId || defaultDeviceId,
      data: { nodes: nodes, edges: edges }
    };
    console.log({ newProgram });
    updateProgram(newProgram);
    toast.success("Program saved successfully");
  };
  const onRunProgram = () => {
    if (running == false) {
      // Use selected device or fallback to default device
      const deviceIdToUse = selectedDeviceId || defaultDeviceId;
      var newProgram = {
        ...program,
        deviceId: deviceIdToUse,
        data: { nodes: nodes, edges: edges },
      };
      console.log({ newProgram });
      runProgram(newProgram);
      setRunning(true);
      toast.info("Program started");
    } else {
      setRunning((prevState) => !prevState);
      toast.warning("Program paused");
    }
  };

  const onStopProgram = () => {
    if (running == true) {
      // Use selected device or fallback to default device
      const deviceIdToUse = selectedDeviceId || defaultDeviceId;
      var newProgram = {
        ...program,
        data: { nodes: nodes, edges: edges },
        deviceId: deviceIdToUse,
      };
      stopProgram(newProgram);
      setRunning(false);
      toast.error("Program stopped");
    } else {
      console.log("");
    }
  };

  useEffect(() => {
    if (nodes && programDone) {
      console.log(programDone);
      setRunning(false);
      setNodes(
        nodes.map((el) => {
          el.style = {
            boxShadow: "unset",
            animation: "unset",
            borderColor: "unset",
          };
          return el;
        })
      );
    }
  }, [programDone]);

  const onDeleteNode = (id) => {
    setNodes((nds) => nds.filter((el) => el.id !== id));
    setEdges((eds) => eds.filter((el) => el.source !== id && el.target !== id));
  };
  const onDeleteEdge = (id) => {
    setEdges((eds) => eds.filter((el) => el.id !== id));
  };

  const onConnect = useCallback(
    (params) =>
      setEdges((eds) =>
        addEdge(
          {
            ...params,
            data: { onDeleteEdge: onDeleteEdge },
            type: "customEdge",
          },
          eds
        )
      ),
    [setEdges]
  );

  return !props.readMode ? (
    <ReactFlowProvider>
      <div className="program-editor">
        <>
          {currentUserDevice ? (
            <>
              <DesktopOnlyMessage />
            </>
          ) : (
            <>
              {editingMode ? (
                // Code Editor View
                <>
                  <ProgramSidebar
                    program={program}
                    onSave={saveData}
                    scope={scope}
                    onAdd={onAdd}
                    runProgram={onRunProgram}
                    running={running}
                    selectedDeviceId={selectedDeviceId}
                    onDeviceChange={setSelectedDeviceId}
                  />
                  <div id="codeeditor-wrapper" style={{ position: "relative" }}>
                    {/* Back to Canvas Button */}
                    <button
                      onClick={closeCodeEditor}
                      style={{
                        position: "absolute",
                        top: "20px",
                        right: "20px",
                        zIndex: 10,
                        background: "rgba(33, 40, 44, 0.95)",
                        border: "2px solid #53bde4",
                        borderRadius: "8px",
                        padding: "0.75rem 1.25rem",
                        color: "#fff",
                        cursor: "pointer",
                        fontWeight: "bold",
                        display: "flex",
                        alignItems: "center",
                        gap: "0.5rem",
                        fontSize: "0.9rem",
                        boxShadow: "0 0 20px rgba(83, 189, 228, 0.3)",
                        transition: "all 0.3s ease",
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.background = "rgba(83, 189, 228, 0.2)";
                        e.currentTarget.style.boxShadow = "0 0 30px rgba(83, 189, 228, 0.5)";
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background = "rgba(33, 40, 44, 0.95)";
                        e.currentTarget.style.boxShadow = "0 0 20px rgba(83, 189, 228, 0.3)";
                      }}
                    >
                      <ArrowLeftIcon style={{ width: "20px", height: "20px" }} />
                      Back to Canvas
                    </button>

                    <MonacoEditor
                      defaultLanguage="javascript"
                      theme="vs-dark"
                      value={nodeCode}
                      onChange={onCodeChange}
                      loading="Loading..."
                      height="100vh"
                      options={{
                        selectOnLineNumbers: true,
                        autoDetectHighContrast: true,
                        minimap: { enabled: true },
                        fontSize: 14,
                      }}
                    />
                  </div>
                </>
              ) : (
                // Canvas View
                <>
                  <ProgramSidebar
                    program={program}
                    onSave={saveData}
                    scope={scope}
                    onAdd={onAdd}
                    runProgram={onRunProgram}
                    running={running}
                    selectedDeviceId={selectedDeviceId}
                    onDeviceChange={setSelectedDeviceId}
                  />
                  <div id="elements-wrapper" ref={reactFlowWrapper}>
                  {!running ? (
                    <button className="play" onClick={onRunProgram}>
                      <div>
                        Play <PlayIcon className="control-icon" />
                      </div>
                    </button>
                  ) : (
                    <div className="application-is-running-container">
                      <p className="program__is__currently__running">
                        Program is currently running!
                      </p>
                      <button onClick={onStopProgram} className="stop">
                        Stop
                      </button>
                      <button className="pause" onClick={onStopProgram}>
                        <div>
                          Pause <PauseIcon className="control-icon" />
                        </div>
                      </button>
                    </div>
                  )}
                  <ReactFlow
                    nodes={nodes}
                    edges={edges}
                    //@ts-ignore
                    edgeTypes={edgeTypes}
                    nodeTypes={nodeTypes}
                    onConnect={onConnect}
                    onNodesChange={onNodesChange}
                    onEdgesChange={onEdgesChange}
                    nodesDraggable={!running}
                    nodesConnectable={!running}
                    elementsSelectable={!running}
                    defaultEdgeOptions={{
                      data: {
                        onDeleteEdge: onDeleteEdge,
                      },
                      animated: false,
                    }}
                    defaultNodesOptions={{
                      style: {
                        boxShadow: "unset",
                        animation: "unset",
                        borderColor: "unset",
                      },
                    }}
                    onDrop={onDrop}
                    onNodesDelete={onDeleteNode}
                    onInit={setReactFlowInstance}
                    onDragOver={onDragOver}
                    snapToGrid={false}
                    className={!running ? "" : "filter-brightness-style"}
                  >
                    <Background gap={12} size={1} />
                    <MiniMap
                      nodeStrokeColor={(n) => {
                        if (n.type === "startNode") return "#0041d0";
                        if (n.type === "basicNode") return BgColor;
                        if (n.type === "ifNode") return BgColor;
                        if (n.type === "endNode") return "#37a18d";
                      }}
                      nodeColor={(n) => {
                        if (n.type === "basicNode") return BgColor;
                        return "#fff";
                      }}
                    />
                    <Controls showInteractive={false}>
                      <ControlButton>
                        <DeleteIcon />
                      </ControlButton>
                    </Controls>
                  </ReactFlow>
                </div>
                </>
              )}
            </>
          )}
        </>
      </div>
      <ToastContainer
        autoClose={3000}
        position="bottom-right"
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="colored"
      />
    </ReactFlowProvider>
  ) : (
    <ReactFlowProvider>
      <div className="program-editor view">
        <div
          id={
            props.classNameWrapper
              ? "elements-wrapper-program"
              : "elements-wrapper"
          }
          ref={reactFlowWrapper}
        >
          <ReactFlow
            nodes={nodes}
            edges={edges}
            //@ts-ignore
            edgeTypes={edgeTypes}
            nodeTypes={nodeTypes}
            elementsSelectable={false}
            nodesConnectable={false}
            nodesDraggable={false}
            zoomOnScroll={true}
            fitView={true}
            // panOnScroll={false}
            // panOnDrag={false}
            zoomOnDoubleClick={true}
            onInit={setReactFlowInstance}
            snapToGrid={true}
            className={!running ? "" : "filter-brightness-style"}
          >
            <Background gap={12} size={1} />
          </ReactFlow>
        </div>
      </div>
    </ReactFlowProvider>
  );
};
export default Editor;
